import { apiClient } from './client.js';
import type {
  ContentType,
  ContentLanguage,
  IcpSegment,
  IcpTarget,
} from '@sequesto-os/shared';

/* ─── Types ─── */

export interface ContentItemFilters {
  type?: ContentType;
  kind?: 'rewrite';
  status?: 'draft' | 'review' | 'approved' | 'published';
  language?: ContentLanguage;
  icpSegment?: IcpSegment;
  person?: 'patrick' | 'evrard' | 'sequesto';
  topicClusterId?: string;
  search?: string;
  /** Include rows hidden from the library (mostly published pages). */
  includeHidden?: boolean;
  /**
   * Archive scope. Defaults to 'active' on the server (omit to keep that).
   * 'archived' shows items pushed to Sanity; 'any' shows both.
   */
  archived?: 'active' | 'archived' | 'any';
  /** Only website page types (any type). */
  channel?: 'website';
  /** Only these item ids (the Library's "Live on the site" list). */
  ids?: string[];
  limit?: number;
  offset?: number;
}

export interface ContentItem {
  id: string;
  campaignId: string | null;
  sequenceId: string | null;
  type: ContentType;
  title: string;
  body: string | null;
  status: string;
  externalRef: string | null;
  publishAt: string | null;
  language: string;
  icpSegment: string | null;
  icpRole: string | null;
  icpBuyingStage: string | null;
  icpTier: string | null;
  topicClusterId: string | null;
  parentContentId: string | null;
  seoMetadata: Record<string, unknown>;
  metadata: Record<string, unknown>;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContentItemsResponse {
  items: ContentItem[];
  total: number;
}

/** Grouped counts for the Library overview (LinkedIn per person, Website per page-type). */
export interface LibrarySummary {
  linkedin: {
    total: number;
    posted: number;
    byPerson: Array<{ person: string; total: number; posted: number }>;
    byStatus?: LibraryStatusCounts;
  };
  website: {
    total: number;
    byType: Array<{ type: ContentType; total: number }>;
    byStatus?: LibraryStatusCounts;
    /**
     * Pages with a published document in Sanity (read live from Sanity, not the
     * OS status). `count` is null with `error` set when Sanity couldn't be read.
     */
    live?: { count: number | null; ids: string[]; error: string | null };
  };
}

export interface LibraryStatusCounts {
  draft: number;
  review: number;
  approved: number;
  published: number;
}

export interface TopicCluster {
  id: string;
  name: string;
  slug: string;
  pillarKeyword: string | null;
  description: string | null;
  language: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ResearchBrief {
  topic: string;
  language: string;
  topicOverview: {
    findings: string[];
    statistics: Array<{ value: string; source: string }>;
    sources: Array<{ title: string; url: string; snippet: string }>;
  };
  competitorLandscape: {
    findings: string[];
    sources: Array<{ title: string; url: string; snippet: string }>;
  } | null;
  icpContext: {
    findings: string[];
    statistics: Array<{ value: string; source: string }>;
  } | null;
  generatedAt: string;
}

export interface GenerateInput {
  contentType: ContentType;
  topic: string;
  notes?: string;
  language?: ContentLanguage;
  persona?: 'patrick' | 'evrard' | 'sequesto';
  icp?: IcpTarget;
  topicClusterId?: string;
  useResearch?: boolean;
  useSerpAnalysis?: boolean;
  serpTargetKeyword?: string;
  serpTargetScore?: number;
  serpMaxIterations?: number;
  extra?: Record<string, unknown>;
  numVariations?: number;
  targetScore?: number;
  maxRevisions?: number;
}

export interface RepurposeInput {
  topic: string;
  contentTypes: ContentType[];
  languages: ContentLanguage[];
  icp?: IcpTarget;
  topicClusterId?: string;
  useResearch?: boolean;
  extra?: Record<string, unknown>;
}

export interface RepurposeResult {
  plan: {
    sourceTopic: string;
    items: Array<{ contentType: ContentType; language: ContentLanguage; topic: string }>;
  };
  parentContentId: string;
  scheduledItems: Array<{
    contentItemId: string;
    jobId: string | null;
    contentType: ContentType;
    language: ContentLanguage;
  }>;
}

/* ─── API Functions ─── */

export async function getContentItems(filters: ContentItemFilters = {}): Promise<ContentItemsResponse> {
  return apiClient.get('/content/items', {
    params: {
      type: filters.type,
      kind: filters.kind,
      status: filters.status,
      language: filters.language,
      icpSegment: filters.icpSegment,
      person: filters.person,
      topicClusterId: filters.topicClusterId,
      search: filters.search?.trim() || undefined,
      includeHidden: filters.includeHidden ? 'true' : undefined,
      archived: filters.archived,
      channel: filters.channel,
      ids: filters.ids ? filters.ids.join(',') : undefined,
      limit: filters.limit ?? 50,
      offset: filters.offset ?? 0,
    },
  });
}

export async function getContentItem(id: string): Promise<ContentItem> {
  return apiClient.get(`/content/items/${id}`);
}

/** Grouped counts for the Library overview. */
export async function getContentLibrarySummary(): Promise<LibrarySummary> {
  return apiClient.get('/content/items/summary');
}

export async function deleteContentItem(id: string): Promise<{ id: string; deleted: boolean }> {
  return apiClient.delete(`/content/items/${id}`);
}

export interface ReviewerInput {
  userId: string;
  name: string;
  email: string;
}

export async function patchContentItem(
  id: string,
  updates: Partial<Pick<ContentItem, 'status' | 'title' | 'body' | 'language' | 'icpSegment' | 'icpRole' | 'icpBuyingStage' | 'icpTier' | 'topicClusterId'>> & {
    seoMetadata?: Record<string, unknown>;
    publishAt?: string | null;
    reviewers?: ReviewerInput[];
    reviewNote?: string;
    editSource?: 'manual_edit' | 'system' | 'revision_loop';
  },
): Promise<ContentItem> {
  return apiClient.patch(`/content/items/${id}`, updates);
}

export interface ContentVersion {
  id: string;
  contentItemId: string;
  versionNum: number;
  title: string;
  body: string | null;
  seoMetadata: Record<string, unknown>;
  snapshot?: Record<string, unknown> | null;
  editedBy: string | null;
  editSource: string;
  diffSummary: string | null;
  createdAt: string;
}

export async function getContentVersions(id: string): Promise<{ versions: ContentVersion[] }> {
  return apiClient.get(`/content/items/${id}/versions`);
}

export interface RestoreContentVersionResponse {
  item: ContentItem;
  restoredVersion: {
    id: string;
    versionNum: number;
    createdAt: string;
    editSource: string;
  };
  snapshotVersion: number;
}

export async function restoreContentVersion(
  contentItemId: string,
  versionId: string,
): Promise<RestoreContentVersionResponse> {
  return apiClient.post(`/content/items/${contentItemId}/versions/${versionId}/restore`, {});
}

export interface SanitySchemaProposal {
  documentTypeName: string;
  suggestedFilename: string;
  tsCode: string;
  notes: string;
}

export async function getSanitySchemaProposal(pageType: string): Promise<SanitySchemaProposal> {
  return apiClient.get(`/content/sanity-schema-proposal`, { params: { pageType } });
}

export interface SanityExportResponse {
  status?: 'schema_missing';
  message?: string;
  proposal?: SanitySchemaProposal;
  sanityDocumentId?: string;
  documentType?: string;
  created?: boolean;
}

export async function exportToSanityWithSchemaCheck(
  id: string,
  documentType: string,
): Promise<SanityExportResponse> {
  return apiClient.post(`/content/items/${id}/export-sanity`, { documentType });
}

export interface BulkGenerateInput {
  pageType: ContentType;
  notionDatabaseId?: string;
  language?: ContentLanguage;
  icp?: IcpTarget;
  useResearch?: boolean;
  useSerpAnalysis?: boolean;
  serpTargetScore?: number;
  serpMaxIterations?: number;
  notes?: string;
  extra?: Record<string, unknown>;
  limit?: number;
  dryRun?: boolean;
}

export interface BulkGenerateResponse {
  databaseId: string;
  pageType: ContentType;
  total: number;
  scheduled?: number;
  failed?: number;
  items?: Array<{ contentItemId: string; jobId: string; title: string; notionPageId: string | null }>;
  failures?: Array<{ title: string; error: string }>;
  // dry-run shape
  pageTypeLabel?: string;
  wouldProcess?: number;
  preview?: Array<{ title: string; notionPageId: string | null }>;
}

export async function bulkGenerateFromNotion(input: BulkGenerateInput): Promise<BulkGenerateResponse> {
  return apiClient.post('/content/bulk-generate-from-notion', input);
}

export interface WebsiteImportInput {
  urls: string[];
  generateImmediately?: boolean;
  refreshMode?: 'rewrite' | 'scratch' | 'manual';
  useResearch?: boolean;
  useSerpAnalysis?: boolean;
  startFromScratch?: boolean;
  serpTargetScore?: number;
  serpMaxIterations?: number;
  notes?: string;
  runInstructions?: string;
  notionDatabaseIds?: string[];
}

export interface WebsiteImportResponse {
  requested: number;
  importedCount: number;
  failedCount: number;
  scheduledCount: number;
  imported: Array<{
    url: string;
    contentItemId: string;
    title: string;
    contentType: string;
    routeFamily: string;
    created: boolean;
    jobId: string | null;
  }>;
  failures: Array<{ url: string; error: string }>;
}

export async function importWebsiteUrls(input: WebsiteImportInput): Promise<WebsiteImportResponse> {
  return apiClient.post('/content/website/import-urls', input);
}

export interface GenerateRefreshInput {
  useResearch?: boolean;
  useSerpAnalysis?: boolean;
  startFromScratch?: boolean;
  /**
   * 'normal' = full LLM rewrite (research + SERP + Notion). Default.
   * 'notion_patch' = minimum-change update for Notion / keyword changes; preserves existing copy. Skips research + SERP.
   * 'seo_geo_optimize' = preserve most copy, but run research + SERP + semantic scoring to improve keyword coverage, answer-first clarity, entity coverage, and other ranking signals.
   */
  regenerationMode?: 'normal' | 'notion_patch' | 'seo_geo_optimize';
  serpTargetScore?: number;
  serpMaxIterations?: number;
  notes?: string;
  runInstructions?: string;
  notionDatabaseIds?: string[];
}

export interface RefreshSerpEntitiesResult {
  contentItemId: string;
  keyword: string;
  competitorPagesCount: number;
  entityCount: number;
}

export async function refreshWebsiteReviewSerpEntities(
  contentItemId: string,
): Promise<RefreshSerpEntitiesResult> {
  return apiClient.post(`/content/items/${contentItemId}/website-review/refresh-serp`, {});
}

export interface WebsiteScoreSummary {
  seo_score: number;
  geo_score: number;
  overall_score: number;
  icp_relevance?: number;
  data_richness?: number;
  readability_score?: number;
  internal_link_score?: number;
  semantic_coverage?: number;
  geo_citability?: number;
}

export interface OptimizationReportScoreDelta {
  before: number;
  after: number;
}

export interface OptimizationReportPass {
  pass: number;
  accepted: boolean;
  acceptanceRule?: 'hard_gap_closed' | 'overall_improvement';
  hardGapsBefore: number;
  hardGapsAfter: number;
  overallBefore: number;
  overallAfter: number;
  semanticBefore?: number;
  semanticAfter?: number;
  citabilityBefore?: number;
  citabilityAfter?: number;
}

export interface OptimizationReport {
  mode: 'seo_geo_optimize';
  passCount: number;
  maxFixPasses: number;
  gapsClosed: string[];
  gapsOpen: string[];
  scoreDeltas: {
    seo: OptimizationReportScoreDelta;
    geo: OptimizationReportScoreDelta;
    icp_relevance: OptimizationReportScoreDelta;
    data_richness: OptimizationReportScoreDelta;
    readability_score: OptimizationReportScoreDelta;
    overall_score: OptimizationReportScoreDelta;
    internal_link_score?: OptimizationReportScoreDelta;
    semantic_coverage?: OptimizationReportScoreDelta;
    geo_citability?: OptimizationReportScoreDelta;
  };
  weakestSectionsAtEnd: Array<{ heading: string; rawScore: number }>;
  passes: OptimizationReportPass[];
}

export interface SemanticCoverageSummary {
  rawScore: number;
  normalisedScore: number;
}

export interface KeywordChangeSummary {
  from: string | null;
  to: string | null;
}

export interface RecalculateWebsiteReviewScoreResult {
  contentItemId: string;
  score: WebsiteScoreSummary;
  readability: {
    fleschKincaidGrade: number;
    algorithmicScore: number;
    wordCount: number;
  };
  semanticCoverage: SemanticCoverageSummary | null;
  semanticSkipReason: string | null;
  keywordWarnings: string[];
  currentKeyword: string | null;
  keywordChanged: KeywordChangeSummary | null;
  review: WebsiteReviewResponse;
}

export interface WebsiteReviewTransparency {
  whatChanged: string;
  optimizationFocus: string;
  scoreExplanation: string;
  sourceUsage: Array<{
    label: string;
    detail: string;
  }>;
  howToImprove: string[];
}

export async function generateWebsiteRefresh(
  id: string,
  input: GenerateRefreshInput = {},
): Promise<{ contentItemId: string; jobId: string; status: string }> {
  return apiClient.post(`/content/items/${id}/generate-refresh`, input);
}

export interface WebsiteReviewResponse {
  contentItemId: string;
  title: string;
  sourceUrl: string | null;
  routeFamily: string | null;
  snapshotKind: 'article' | 'glossary' | 'structured';
  sanityDocumentType: string;
  sanityDocumentId: string | null;
  existing: Record<string, unknown>;
  proposed: Record<string, unknown>;
  sectionOrder: string[];
  fieldOrderByPath: Record<string, string[]>;
  diff: RewriteDiff;
  score: WebsiteScoreSummary | null;
  transparency: WebsiteReviewTransparency | null;
  transparencyError: string | null;
  previewIframeUrl: string | null;
  studioDraftUrl: string | null;
}

export async function getWebsiteReview(contentItemId: string): Promise<WebsiteReviewResponse> {
  return apiClient.get(`/content/items/${contentItemId}/website-review`);
}

export async function saveWebsiteReviewSnapshot(input: {
  contentItemId: string;
  snapshot: Record<string, unknown>;
  source: 'manual_edit' | 'paste_replacement' | 'section_edit';
}): Promise<{
  versionNum: number;
  diffSummary: string;
  review: WebsiteReviewResponse;
  learning: {
    modificationId: string | null;
    ruleCreated: boolean;
    ruleId: string | null;
    diffSummary: string;
    skippedNoChange: boolean;
  };
}> {
  return apiClient.post(`/content/items/${input.contentItemId}/website-review/save`, {
    snapshot: input.snapshot,
    source: input.source,
  });
}

export type VoicePersona = 'patrick' | 'evrard' | 'sequesto';

/** Import a persona's real LinkedIn posts into the published library (posts separated by a line of "---"). */
export async function importVoiceSamples(input: {
  person: VoicePersona;
  text: string;
}): Promise<{ inserted: number; skipped: number }> {
  return apiClient.post('/content/voice-samples/import', input);
}

/** Count existing voice samples for a persona. */
export async function getVoiceSampleCount(person: VoicePersona): Promise<{ person: string; count: number }> {
  return apiClient.get(`/content/voice-samples/count?person=${encodeURIComponent(person)}`);
}

export interface FinishLinkedInReviewResult {
  versionNum: number;
  review: { status: string; message: string };
}

/**
 * Save the reviewer's human-authored rewrite of a LinkedIn post and finish
 * their review step. The edited text becomes a voice sample for the persona.
 */
export async function finishLinkedInReview(input: {
  contentItemId: string;
  editedText: string;
}): Promise<FinishLinkedInReviewResult> {
  return apiClient.post(`/content/items/${input.contentItemId}/linkedin-review/finish`, {
    editedText: input.editedText,
  });
}

export interface PushWebsiteReviewDraftSuccess {
  sanityDocumentId: string;
  created: boolean;
  previewIframeUrl: string | null;
  studioDraftUrl: string | null;
  activatedRuleIds: string[];
  duplicateRuleIds: string[];
}

export interface PushWebsiteReviewDraftConflict {
  requiresConfirmation: true;
  otherMode: 'scratch' | 'rewrite' | 'manual';
  otherPushedAt: string | null;
  otherTitle: string;
  otherId: string;
}

export type PushWebsiteReviewDraftResult =
  | PushWebsiteReviewDraftSuccess
  | PushWebsiteReviewDraftConflict;

export async function pushWebsiteReviewDraft(
  contentItemId: string,
  options?: { confirmOverwrite?: boolean },
): Promise<PushWebsiteReviewDraftResult> {
  return apiClient.post(
    `/content/items/${contentItemId}/website-review/push-draft`,
    options?.confirmOverwrite ? { confirmOverwrite: true } : {},
    { passthroughStatuses: [409] },
  );
}

export async function recalculateWebsiteReviewScore(
  contentItemId: string,
): Promise<RecalculateWebsiteReviewScoreResult> {
  return apiClient.post(`/content/items/${contentItemId}/website-review/recalculate-score`, {});
}

export async function publishWebsiteReviewDraft(contentItemId: string): Promise<{
  contentItemId: string;
  sanityDocumentId: string;
  diffSummary: string;
  ruleCreated: boolean;
  modificationId: string | null;
  previewIframeUrl: string | null;
}> {
  return apiClient.post(`/content/items/${contentItemId}/website-review/publish`, {});
}

export async function reopenWebsiteReview(contentItemId: string): Promise<ContentItem> {
  return apiClient.post(`/content/items/${contentItemId}/website-review/reopen`, {});
}

export async function approveContentItem(id: string): Promise<ContentItem> {
  return apiClient.post(`/content/items/${id}/approve`);
}

export interface SanityDocumentType {
  name: string;
  title?: string;
  fieldCount: number;
}

export async function getSanityTypes(): Promise<{ types: SanityDocumentType[] }> {
  return apiClient.get('/content/sanity-types');
}

export interface SanityDocumentTypeInfo extends SanityDocumentType {
  count: number;
  isSingleton: boolean;
}

export async function listSanityDocumentTypes(): Promise<{ types: SanityDocumentTypeInfo[] }> {
  return apiClient.get('/content/sanity/document-types');
}

export interface DuplicateSanityStructureResponse {
  contentItemId: string;
  sanityDocumentId: string;
  sanityDocumentType: string;
  studioUrl: string | null;
  sourceDocumentId: string;
  sourceTitle: string | null;
}

export async function duplicateSanityStructure(
  sanityType: string,
): Promise<DuplicateSanityStructureResponse> {
  return apiClient.post('/content/sanity/duplicate-structure', { sanityType });
}

export interface GenerateFromStructureInput {
  pageName: string;
  seoKeyword?: string;
  competitor?: string;
  description?: string;
  runInstructions?: string;
  language?: ContentLanguage;
  useResearch?: boolean;
  useSerpAnalysis?: boolean;
  notionDatabaseIds?: string[];
  extraFields?: Record<string, string>;
}

export interface GenerateFromStructureResponse {
  contentItemId: string;
  jobId: string | null;
  status: 'generating';
}

export async function generateFromStructure(
  contentItemId: string,
  input: GenerateFromStructureInput,
): Promise<GenerateFromStructureResponse> {
  return apiClient.post(`/content/items/${contentItemId}/generate-from-structure`, input);
}

export async function exportToSanity(id: string, documentType?: string): Promise<{
  sanityDocumentId: string;
  documentType?: string;
  created: boolean;
  message: string;
}> {
  return documentType
    ? apiClient.post(`/content/items/${id}/export-sanity`, { documentType })
    : apiClient.post(`/content/items/${id}/export-sanity`);
}

export async function getDerivedItems(parentId: string): Promise<{ parentId: string; derived: ContentItem[]; total: number }> {
  return apiClient.get(`/content/items/${parentId}/derived`);
}

export async function generateContent(input: GenerateInput): Promise<Record<string, unknown>> {
  return apiClient.post('/content/generate', input);
}

export type ClaimVerdict = 'supported' | 'unsupported' | 'contradicted' | 'unverifiable';

export interface VerifiedClaim {
  id: string;
  verbatim: string;
  kind: string;
  verdict: ClaimVerdict;
  method: string;
  note?: string;
  evidence: Array<{ provider: string; url?: string; text: string }>;
}

export interface VerificationReport {
  status: 'passed' | 'blocked' | 'cleared';
  counts: Record<ClaimVerdict, number>;
  claims: VerifiedClaim[];
  repairs: Array<{ claimId: string; action: string; before: string; after: string; reason: string }>;
  evidenceByProvider?: Record<string, number>;
  verifiedAt: string;
  error?: string;
  clearedAt?: string;
  clearedBy?: string;
}

export interface ContentVerification {
  verification: VerificationReport | null;
  keywordBrief: {
    topic: string;
    headTerm: string;
    headVolume: number | null;
    headDifficulty: number | null;
    secondaryKeywords: Array<{ keyword: string; searchVolume: number }>;
    questionKeywords: Array<{ keyword: string; searchVolume: number }>;
    warnings: string[];
  } | null;
  optimizationReport: Record<string, unknown> | null;
  brokenLinks: string[];
}

export async function getContentVerification(id: string): Promise<ContentVerification> {
  return apiClient.get(`/content/items/${id}/verification`);
}

export async function clearVerificationBlock(id: string): Promise<{ verification: VerificationReport }> {
  return apiClient.post(`/content/items/${id}/verification/clear`);
}

export interface ImpactKeywordCandidate {
  keyword: string;
  searchVolume: number;
  difficulty: number | null;
}

export interface ImpactArticleResult {
  contentItemId: string;
  jobId: string | null;
  contentType: string;
  topic: string;
  keywordBrief: {
    headTerm: string;
    headVolume: number | null;
    headDifficulty: number | null;
    secondaryKeywords: ImpactKeywordCandidate[];
    questionKeywords: ImpactKeywordCandidate[];
    warnings: string[];
  };
  peecContext: {
    referenceUrls: string[];
    origin: string;
    urlClassification?: string;
    sourceUrl?: string;
  } | null;
}

/**
 * Start a fully-researched, fact-gated article. `input` is either a title, a
 * Peec AI link, or a competitor URL — the server works out which.
 */
export async function createImpactArticle(input: {
  input: string;
  contentType?: 'seo_article' | 'comparison_page';
  language?: ContentLanguage;
  keywordDatabase?: string;
  keywordSeed?: string;
  outlineDirective?: string;
}): Promise<ImpactArticleResult> {
  return apiClient.post('/content/impact-article', input);
}

export async function researchTopic(input: {
  topic: string;
  language?: ContentLanguage;
  contentType?: ContentType;
  icpSegments?: IcpSegment[];
  skipCache?: boolean;
}): Promise<ResearchBrief> {
  return apiClient.post('/content/research', input);
}

export async function repurposeContent(input: RepurposeInput): Promise<RepurposeResult> {
  return apiClient.post('/content/repurpose', input);
}

export async function getTopicClusters(): Promise<{ clusters: TopicCluster[] }> {
  return apiClient.get('/content/topic-clusters');
}

export async function getTopicCluster(id: string): Promise<TopicCluster> {
  return apiClient.get(`/content/topic-clusters/${id}`);
}

export async function createTopicCluster(input: {
  name: string;
  slug: string;
  pillarKeyword?: string;
  description?: string;
  language?: ContentLanguage;
}): Promise<TopicCluster> {
  return apiClient.post('/content/topic-clusters', input);
}

export async function patchTopicCluster(
  id: string,
  updates: Partial<Pick<TopicCluster, 'name' | 'pillarKeyword' | 'description' | 'language'>>,
): Promise<TopicCluster> {
  return apiClient.patch(`/content/topic-clusters/${id}`, updates);
}

export async function getGeneratorTypes(): Promise<{ types: string[] }> {
  return apiClient.get('/content/generator-types');
}

/* ─── Rewrite from URL ─── */

export interface UrlRoute {
  prefix: string;
  detailSanityType: string;
  overviewSanityType?: string;
}

export type RewriteFieldDiffKind = 'added' | 'removed' | 'changed' | 'unchanged';

export interface RewriteFieldDiff {
  path: string;
  kind: RewriteFieldDiffKind;
  before?: unknown;
  after?: unknown;
}

export interface RewriteSectionDiff {
  sectionKey: string;
  kind: RewriteFieldDiffKind;
  fields: RewriteFieldDiff[];
}

export interface RewriteDiff {
  sectionsChanged: string[];
  sectionsAdded: string[];
  sectionsRemoved: string[];
  perSection: RewriteSectionDiff[];
  summary: string;
}

export interface RewriteSource {
  database: string;
  databaseId: string;
  pageTitle: string;
  matchType: 'exact' | 'contains' | 'overlap' | 'list' | 'overview' | 'feature';
  fieldsUsed: string[];
  pageId?: string;
}

export type RewritePreviewResponse =
  | {
      status: 'questions';
      questions: { field: string; prompt: string; suggestion: string | null; source: string }[];
      existingSummary: { title: string; sectionKeys: string[]; url: string };
      sanityDocumentId: string;
      documentType: string;
      contentType: string | null;
      slug: string | null;
      language: string;
    }
  | {
      status: 'preview';
      contentItemId: string;
      reusedProject: boolean;
      sanityDocumentId: string;
      documentType: string;
      contentType: string | null;
      slug: string | null;
      language: string;
      title: string;
      existing: Record<string, unknown>;
      proposed: Record<string, unknown>;
      diff: RewriteDiff;
      sources: RewriteSource[];
      seoKeyword: string | null;
      keywordWarnings: string[];
      url: string;
      itemStatus: 'draft' | 'review' | 'approved' | 'published';
      reviewMeta: {
        requestedBy: string;
        requestedAt: string;
        note?: string | null;
        reviewers: Array<{
          userId: string;
          name: string;
          email: string;
          status: 'pending' | 'approved';
          approvedAt?: string;
        }>;
      } | null;
      previewIframeUrl: string | null;
    };

export type RewritePushResponse =
  | { status: 'pushed'; sanityDocumentId: string; studioPath: string; overwroteDraft: boolean }
  | { status: 'draft_exists'; existingDraftUpdatedAt: string; message: string };

export interface SaveProposedEnvelopeResult {
  versionNum: number;
  diffSummary: string;
  keywordWarnings: string[];
}

export async function getRewriteRouteTable(): Promise<{ routes: UrlRoute[] }> {
  return apiClient.get('/content/rewrite/route-table');
}

export interface NotionBriefSource {
  id: string;
  label: string;
  description: string;
  databaseId: string | null;
  defaultOn: boolean;
  role: 'primary' | 'supplemental';
}

export async function getRewriteNotionSources(): Promise<{ sources: NotionBriefSource[] }> {
  return apiClient.get('/content/rewrite/notion-sources');
}

export async function previewRewrite(input: {
  url: string;
  contentTypeOverride?: string;
  notionDatabaseIds?: string[];
  answers?: Record<string, string>;
  forceNewProject?: boolean;
}): Promise<RewritePreviewResponse> {
  return apiClient.post('/content/rewrite/preview', input);
}

/**
 * The 'preview' arm of `RewritePreviewResponse`. Returned by `getRewriteProject`
 * when re-opening a stored rewrite project from the library.
 */
export type RewriteProject = Extract<RewritePreviewResponse, { status: 'preview' }>;

export async function getRewriteProject(contentItemId: string): Promise<RewriteProject> {
  return apiClient.get(`/content/rewrite/projects/${contentItemId}`);
}

export async function saveProposedEnvelope(input: {
  contentItemId: string;
  envelope: Record<string, unknown>;
  source: 'manual_edit' | 'paste_replacement' | 'section_edit';
}): Promise<SaveProposedEnvelopeResult> {
  return apiClient.post(`/content/items/${input.contentItemId}/proposed-envelope`, {
    envelope: input.envelope,
    source: input.source,
  });
}

export async function pushRewrite(input: {
  contentItemId: string;
  force?: boolean;
}): Promise<RewritePushResponse> {
  return apiClient.post('/content/rewrite/push', input, { passthroughStatuses: [409] });
}

/* ─── Modifications timeline + auto-learning ─── */

export type ModificationSource = 'rewrite_push' | 'studio_poll' | 'manual';

export interface ModificationListItem {
  id: string;
  source: ModificationSource;
  diffSummary: string | null;
  sectionsChanged: string[];
  detectedAt: string;
  extractionStatus: 'pending' | 'completed' | 'updated_existing' | 'duplicate' | 'no_rule' | 'failed';
  extractionError: string | null;
  beforeEnvelope: Record<string, unknown>;
  afterEnvelope: Record<string, unknown>;
  rule: {
    id: string;
    rule: string;
    category: string;
    priority: number;
    status: string;
    isActive: boolean;
  } | null;
}

export async function listModifications(
  contentItemId: string,
): Promise<{ modifications: ModificationListItem[] }> {
  return apiClient.get(`/content/items/${contentItemId}/modifications`);
}

export interface PollNowResponse {
  contentItemId: string;
  changed: boolean;
  modificationId: string | null;
  ruleCreated: boolean;
  ruleId: string | null;
  skippedReason: string | null;
}

export async function pollRewriteProjectNow(contentItemId: string): Promise<PollNowResponse> {
  return apiClient.post(`/content/items/${contentItemId}/poll`, {});
}

export async function reExtractModification(
  modificationId: string,
): Promise<{
  ruleCreated: boolean;
  ruleUpdated: boolean;
  ruleId: string | null;
  extractionStatus: 'pending' | 'completed' | 'updated_existing' | 'duplicate' | 'no_rule' | 'failed';
}> {
  return apiClient.post(`/content/modifications/${modificationId}/re-extract`, {});
}

export async function deleteModification(
  modificationId: string,
): Promise<{ id: string; deleted: boolean }> {
  return apiClient.delete(`/content/modifications/${modificationId}`);
}
